import React, { useState } from "react";
import * as Sentry from "@sentry/react-native";
import {
  Alert,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { Feather } from "@expo/vector-icons";
import { useRouter } from "expo-router";

import EmailInput from "../../Authentication/Components/FormControl/Components/EmailInput";
import PasswordInput from "../../Authentication/Components/FormControl/Components/PasswordInput";

import ChurnReasonSelector, {
  CHURN_MIN_FEEDBACK_LENGTH,
} from "./Components/ChurnReasonSelector";

import ScreenLayout from "@/Components/Layouts/Native/ScreenLayout";
import { useSession } from "@/context/auth/useSession";
import { ApiError } from "@/schemas/api/error";
import { type AppUserSession } from "@/schemas/auth/session";
import { isValidEmailAddress } from "@/schemas/forms/email";
import { isValidPassword } from "@/schemas/forms/password";
import { type DeleteAccountRequest } from "@/schemas/user/identity";
import IdentityService from "@/services/user/identity";
import { alertUnexpectedApiError } from "@/utils/alerts";

type DeleteAccountScreenProps = {
  session: AppUserSession;
};

function DeleteAccountScreen({
  session,
}: DeleteAccountScreenProps): React.JSX.Element {
  const router = useRouter();
  const { signOut } = useSession();

  const [reasonId, setReasonId] = useState<string | null>(null);
  const [userFeedback, setUserFeedback] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");

  const isChurnFormComplete =
    reasonId !== null &&
    userFeedback.trim().length >= CHURN_MIN_FEEDBACK_LENGTH;

  const validateInputs = (): boolean => {
    if (!reasonId) {
      Alert.alert("Missing Reason", "Please select a reason for leaving.", [
        { text: "OK" },
      ]);
      return false;
    }
    if (userFeedback.trim().length < CHURN_MIN_FEEDBACK_LENGTH) {
      Alert.alert(
        "Feedback Too Short",
        `Please provide at least ${CHURN_MIN_FEEDBACK_LENGTH} characters of feedback.`,
        [{ text: "OK" }],
      );
      return false;
    }
    if (email && password) {
      const emailValidation = isValidEmailAddress(email);
      const passwordValidation = isValidPassword(password);

      if (emailValidation.isValid === false) {
        Alert.alert("Invalid Email Address", emailValidation.message, [
          { text: "OK" },
        ]);
        return false;
      }
      if (passwordValidation.isValid === false) {
        Alert.alert("Invalid Password", passwordValidation.message, [
          { text: "OK" },
        ]);
        return false;
      }
    } else {
      if (!email) {
        Alert.alert("Missing Email", "Please enter your email.", [
          { text: "OK" },
        ]);
        return false;
      }
      if (!password) {
        Alert.alert("Missing Password", "Please enter your password.", [
          { text: "OK" },
        ]);
        return false;
      }
    }
    return true;
  };

  const onSubmit = async (): Promise<void> => {
    const isValid = validateInputs();
    if (isValid === false || !reasonId) {
      return;
    }

    try {
      const identityService = new IdentityService(session);
      const request: DeleteAccountRequest = {
        email,
        password,
        reason_id: reasonId,
        user_feedback: userFeedback.trim(),
      };
      await identityService.deleteAccount(request);
      await signOut();
      router.replace("/");
    } catch (error) {
      Sentry.captureException(error);
      const apiError = error as ApiError;
      switch (apiError.type) {
        default:
          alertUnexpectedApiError(apiError);
      }
    }
  };

  return (
    <ScreenLayout isLoading={false}>
      <ScrollView style={styles.content}>
        <ChurnReasonSelector
          reasonId={reasonId}
          onReasonIdChange={setReasonId}
          userFeedback={userFeedback}
          onUserFeedbackChange={setUserFeedback}
        />

        <View style={styles.alert}>
          <View style={styles.alertHeader}>
            <Feather name="alert-circle" size={20} color="#ef4444" />
            <Text style={styles.alertTitle}>Warning</Text>
          </View>
          <Text style={styles.alertDescription}>
            Deleting your account is permanent and cannot be undone.
          </Text>
        </View>

        <View style={styles.infoBox}>
          <View style={styles.infoBoxHeader}>
            <Text style={styles.infoBoxTitle}>
              What happens when I delete my account?
            </Text>
          </View>
          <View style={styles.infoBoxContent}>
            <Text style={styles.infoBoxItem}>
              • All your personal information will be erased.
            </Text>
            <Text style={styles.infoBoxItem}>
              • Your posts and likes will be removed.
            </Text>
            <Text style={styles.infoBoxItem}>
              • You will lose access to all your data.
            </Text>
            <Text style={styles.infoBoxItem}>
              • Deletion requests usually take 30 days.
            </Text>
          </View>
        </View>

        <View style={styles.confirmSection}>
          <Text style={styles.confirmText}>
            To confirm deletion, please provide your login credentials:
          </Text>
          <Text style={styles.label}>Email</Text>
          <KeyboardAvoidingView
            behavior={Platform.OS === "ios" ? "padding" : "height"}
            style={styles.formContainer}
          >
            <EmailInput
              value={email}
              onChangeText={setEmail}
              placeholder="Enter your email"
            />
            <Text style={styles.label}>Password</Text>
            <PasswordInput
              value={password}
              onChangeText={setPassword}
              placeholder="Enter your password"
            />
            <View style={styles.footer}>
              <TouchableOpacity
                style={[
                  styles.button,
                  styles.deleteButton,
                  !isChurnFormComplete && styles.deleteButtonDisabled,
                ]}
                onPress={onSubmit}
                disabled={!isChurnFormComplete}
              >
                <Text style={styles.deleteButtonText}>
                  Permanently Delete My Account
                </Text>
              </TouchableOpacity>
            </View>
          </KeyboardAvoidingView>
        </View>
      </ScrollView>
    </ScreenLayout>
  );
}

const styles = StyleSheet.create({
  formContainer: {
    width: "100%",
    maxWidth: 400,
  },
  content: {
    flex: 1,
    padding: 16,
  },
  alert: {
    backgroundColor: "#fef2f2",
    borderColor: "#fef2f2",
    borderRadius: 8,
    padding: 16,
    marginBottom: 24,
  },
  alertHeader: {
    flexDirection: "row",
    alignItems: "center",
  },
  alertTitle: {
    fontSize: 20,
    fontWeight: "600",
    color: "#ef4444",
    marginLeft: 8,
  },
  alertDescription: {
    marginTop: 8,
    fontSize: 14,
    color: "#ef4444",
  },
  infoBox: {
    borderWidth: 1,
    borderColor: "#e5e5e5",
    borderRadius: 8,
    marginBottom: 24,
  },
  infoBoxHeader: {
    left: 16,
    marginTop: 16,
  },
  infoBoxTitle: {
    fontSize: 16,
    fontWeight: "500",
  },
  infoBoxContent: {
    left: 16,
    padding: 16,
  },
  infoBoxItem: {
    fontSize: 14,
    marginBottom: 8,
  },
  confirmSection: {
    marginBottom: 24,
  },
  confirmText: {
    fontSize: 16,
    marginBottom: 16,
  },
  footer: {
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: "#e5e5e5",
  },
  button: {
    borderRadius: 8,
    padding: 16,
    alignItems: "center",
    marginBottom: 12,
  },
  deleteButton: {
    backgroundColor: "#ef4444",
    flexDirection: "row",
    justifyContent: "center",
  },
  deleteButtonDisabled: {
    backgroundColor: "#FCA5A5",
  },
  deleteButtonText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "600",
    marginLeft: 8,
  },
  label: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 5,
    color: "#4A5568",
  },
});

export default DeleteAccountScreen;
